import {
  MutateWidgetPayload,
  QuerySettingsDto,
  RequestType,
  Timespan,
  VariableDto,
} from '@thingslog/repositories';
import React, { FC, useMemo } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { subMonths } from 'date-fns';
import { TimespanFactory } from '../../../../factories/Timespan.factory';
import { useQuerySettingsOptions } from './useQuerySettingsOptions';
import { QuerySettingsFieldsProps, TimespanMode } from './models';
import {
  ApiAlarmsCountQueryForm,
  ApiBatteryHistogramQueryForm,
  ApiDeviceSensorQueryForm,
  ApiDevicesBatteryQueryForm,
  ApiDevicesSignalQueryForm,
  ApiDevicesTransmissionsQueryForm,
  ApiReadingsDeviceGroupsQueryForm,
  ApiReadingsDeviceSensorListQueryForm,
  ApiReadingsDeviceSensorTypeListQueryForm,
  ApiReadingsMixedQueryForm,
  ApiSignalHistogramQueryForm,
} from './QueryTypeForms';
import { createDefaultQuerySettings } from './querySettingsDefaults';

interface QuerySettingsFormProps {
  control: Control<MutateWidgetPayload>;
  setValue: UseFormSetValue<MutateWidgetPayload>;
  queryType?: RequestType;
  variables: VariableDto[];
  disabled?: boolean;
}

const querySettingsFormRegistry: Record<RequestType, FC<QuerySettingsFieldsProps>> = {
  'api-alarms-count': ApiAlarmsCountQueryForm,
  'api-battery-histogram': ApiBatteryHistogramQueryForm,
  'api-device-sensor': ApiDeviceSensorQueryForm,
  'api-devices-battery': ApiDevicesBatteryQueryForm,
  'api-devices-signal': ApiDevicesSignalQueryForm,
  'api-devices-transmissions': ApiDevicesTransmissionsQueryForm,
  'api-signal-histogram': ApiSignalHistogramQueryForm,
  'api-readings-device-groups': ApiReadingsDeviceGroupsQueryForm,
  'api-readings-device-sensor-list': ApiReadingsDeviceSensorListQueryForm,
  'api-readings-device-sensor-type-list': ApiReadingsDeviceSensorTypeListQueryForm,
  'api-readings-mixed': ApiReadingsMixedQueryForm,
};

export const QuerySettingsForm: FC<QuerySettingsFormProps> = ({
  control,
  setValue,
  queryType,
  variables,
  disabled = false,
}: QuerySettingsFormProps) => {
  const { t } = useTranslation();
  const query = useWatch({ control, name: 'settings.query' });
  const optionSources = useQuerySettingsOptions(variables);

  const timespanType = useMemo<TimespanMode>(() => {
    if (!query || !('timespan' in query) || !query.timespan) {
      return 'none';
    }

    return query.timespan.type;
  }, [query]);

  const handleTimespanTypeChange = (type: TimespanMode): void => {
    if (!query || !('timespan' in query)) {
      return;
    }

    let timespan: Timespan | null = null;

    if (type === 'predefined') {
      timespan = TimespanFactory.create({ type: 'predefined', name: 'this-month' });
    }

    if (type === 'custom') {
      timespan = TimespanFactory.create({
        type: 'custom',
        fromDate: subMonths(new Date(), 1),
        toDate: new Date(),
      });
    }

    setValue(
      'settings.query',
      {
        ...query,
        timespan,
      } as QuerySettingsDto,
      { shouldDirty: true, shouldValidate: true }
    );
  };

  if (!queryType) {
    return (
      <div className="text-sm text-gray-600">
        {t(
          'dashboard_select_data_source_to_configure_query',
          'Select a data source to configure the query.'
        )}
      </div>
    );
  }

  const QuerySettingsFields = querySettingsFormRegistry[queryType];

  if (!QuerySettingsFields) {
    return (
      <div className="text-sm text-gray-600">
        {t('dashboard_query_type_not_supported', 'This query type is not supported yet.')}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <QuerySettingsFields
        control={control}
        setValue={setValue}
        disabled={disabled}
        optionSources={optionSources}
        timespanType={timespanType}
        handleTimespanTypeChange={handleTimespanTypeChange}
      />
    </div>
  );
};

export { createDefaultQuerySettings };
